import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { Store } from '../types/Store';
import { useAuthenticatedFetchWithWorkspace } from '../hooks/Keycloak/useAuthenticatedFetchWithWorkspace';
import { useStoreCache } from './StoreCacheContext';

interface CachedList {
    data: Store[];
    timestamp: number;
}

interface StoreListContextType {
    stores: Store[] | null;
    loading: boolean;
    error: string;
    refreshStores: (skipCache?: boolean) => Promise<void>;
}

const StoreListContext = createContext<StoreListContextType | undefined>(undefined);

export const useStoreList = (): StoreListContextType => {
    const ctx = useContext(StoreListContext);
    if (!ctx) throw new Error('useStoreList must be used within StoreListProvider');
    return ctx;
};

export const StoreListProvider = ({ children }: { children: ReactNode }) => {
    const [stores, setStores] = useState<Store[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const { authFetch, isReady, isLoading: workspaceLoading, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const { invalidateAll } = useStoreCache();
    const prevWorkspaceIdRef = useRef<string | null>(null);
    const listCacheRef = useRef<CachedList | null>(null);
    const LIST_CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes

    const fetchStores = useCallback(async (skipCache = false): Promise<void> => {
        try {
            setLoading(true);
            setError('');

            if (!skipCache && listCacheRef.current) {
                const age = Date.now() - listCacheRef.current.timestamp;
                if (age < LIST_CACHE_MAX_AGE) {
                    setStores(listCacheRef.current.data);
                    setLoading(false);
                    return;
                }
            }

            const result: Store[] = await authFetch('/allStores', { method: 'GET' });
            if (!result) throw new Error('Failed to retrieve store data.');
            listCacheRef.current = { data: result, timestamp: Date.now() };
            setStores(result);
        } catch (err: unknown) {
            if (err instanceof Error) setError(err.message);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authFetch]);

    useEffect(() => {
        if (workspaceLoading) {
            setLoading(true);
            return;
        }
        if (!isReady) {
            setLoading(false);
            return;
        }

        const workspaceChanged = prevWorkspaceIdRef.current !== null && prevWorkspaceIdRef.current !== workspaceId;
        prevWorkspaceIdRef.current = workspaceId;

        if (workspaceChanged) {
            invalidateAll(); // vide le cache des stores individuels
            listCacheRef.current = null;
        }

        setStores(null);
        setLoading(true);
        setError('');
        fetchStores(workspaceChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, workspaceLoading, workspaceId]);

    const contextValue = useMemo(
        () => ({ stores, loading, error, refreshStores: fetchStores }),
        [stores, loading, error, fetchStores]
    );

    return (
        <StoreListContext.Provider value={contextValue}>
            {children}
        </StoreListContext.Provider>
    );
};
